/* Copyright (c) 2016-2023 Planticle Pty Ltd */
"use strict";

import Cookies from "js-cookie";

function pad(number, digits) {
	var str = "" + number;
	while(str.length < digits) {
		str = "0" + str;
	}
	return str;
}

function assert(condition, message) {
    if (!condition) {
        message = message || "Assertion failed";
        if (typeof Error !== "undefined") {
            throw new Error(message);
        }
        throw message; // Fallback
    }
}

// http://stackoverflow.com/a/8809472/165783
function uuid(){
    var d = new Date().getTime();
    var uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
        var r = (d + Math.random()*16)%16 | 0;
        d = Math.floor(d/16);
        return (c=='x' ? r : (r&0x3|0x8)).toString(16);
    });
    return uuid;
}

// Converts a simple query string a=b&c=d&e=f into a javascript object
// Doesn't allow for keys with more than one - last value overwrites previous values for a given key
function parseParams(queryString) {
    var params = {}, regex = /([^&=]+)=([^&]*)/g, m;
    while(true) {
        m = regex.exec(queryString);
        if(!m) break;
        params[decodeURIComponent(m[1])] = decodeURIComponent(m[2]);
    }
    return params;
}

function encodeParams(params) {
    return Object.entries(params || {})
    .filter(p => p[1] !== undefined)
    .map(function(p) {
        return encodeURIComponent(p[0]) + "=" + encodeURIComponent(p[1]);
    })
    .toSorted()
    .join("&");
}

function hashParams(hash) {
    if(!hash) hash = window.location.hash;
    if(hash[0] == '#') hash = hash.substring(1);
    return parseParams(hash);
}

function queryParams(search) {
    if(!search) search = window.location.search;
    if(search[0] == '?') search = search.substring(1);
    return parseParams(search);
}

function updateHashParams(params, keysToRemove) {
    params = {
        ...hashParams(),
        ...params
    };
    (keysToRemove || []).forEach(function(key) { delete params[key]; });
    window.location.hash = encodeParams(params);
}

// http://stackoverflow.com/a/12034334/165783
var entityMap = {
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': '&quot;',
    "'": '&#39;',
    "/": '&#x2F;'
  };

function escapeHtml(string) {
    return String(string).replace(/[&<>"'\/]/g, function (s) {
        return entityMap[s];
    });
}

// Alternative implementation:
// function escapeHtml(str) {
//     var div = document.createElement('div');
//     div.appendChild(document.createTextNode(str));
//     return div.innerHTML;
// };

function compareNumber(a, b) {
    return a - b;
}

function compareNumberFn(fn) {
    return function(a, b) {
        return fn(a) - fn(b);
    }
}

function compareStringFn(fn) {
    return function(a, b) {
        return fn(a).localeCompare(fn(b));
    }
}

function formatDuration(t, unit) {
    var s = 0;

    if(unit == "seconds") {
        s = t;
    } else {
        s = t * 60;
    }

    var h = Math.floor(s / 3600);
    var m = Math.floor(s / 60) % 60;
    s = s % 60;

    var parts = [];
    if(h) parts.push(h + "h");
    if(m) parts.push(m + "m");
    if(s) parts.push(s + "s");

    return parts.join(" ");
}

function withThousandSeparators(p) {
    var parts = [];
    var i = p.length % 3;
    if(i > 0) parts.push(p.substr(0,i));
    while(i < p.length) {
        parts.push(p.substr(i,3));
        i += 3;
    }
    return parts.join(",");
}

function formatNumber(n) {
    if(typeof n.toLocaleString === 'function') {
        return n.toLocaleString();
    } else {
        var str = "" + n;
        return str.replace(/^[0-9]+(\.|$)/, withThousandSeparators);
    }
}

function formatDistance(meters) {
    if(isNaN(+meters)) return "";

    var m = Math.abs(meters);
    var suffix;

    if(m < 1000) {
        suffix = "m";
    } else {
        meters /= 1000;
        suffix = "km";
    }

    return meters.toLocaleString(undefined, { maximumSignificantDigits: 3 }) + suffix;
}

function formatQuantity(n, noun, plural) {
    var suffix = (n == 1) ? noun : (plural || noun + "s");
    return formatNumber(n) + " " + suffix;
}

function formatSize(bytes) {
    if(bytes < 1000) {
        return bytes + " B";
    }

    var scale = Math.floor(Math.log10(bytes) / 3);
    var units = ["B", "KB", "MB", "GB"];
    scale = Math.max(Math.min(scale, units.length - 1), 0);
    var unit = units[scale];
    var significand = bytes / Math.pow(10, scale * 3);

    return significand.toPrecision(3) + " " + unit;
}

function formatDMS(x, positiveSuffix, negativeSuffix) {
    var seconds = Math.round(Math.abs(x) * 3600);
    var d = Math.floor(seconds / 3600);
    var m = Math.floor(seconds / 60) % 60;
    var s = seconds % 60;

    return d + "° " + m + "′ " + s + "″ " + (x >= 0 ? positiveSuffix : negativeSuffix);
}

function formatDecimalCoordinates(p) {
    return p ? p.lat.toFixed(6) + ", " + p.lon.toFixed(6) : "";
}

// Displays date (in UTC)
function formatDate(date) {
    if(typeof date === "string") {
        date = parseISODate(date);
    }
    // Note - I originally used 'default' locale but was surprised to discover that Firefox on my Mac was set to en-US.
    // I don't remember ever choosing that, so it is likely to be a problem for others too.
    return date.toLocaleDateString("en-AU", { year: "numeric", month: "2-digit", day: "2-digit", timeZone: "UTC" });
}

function formatISODate(date) {
    return [
        date.getFullYear(),
        pad(date.getMonth() + 1, 2),
        pad(date.getDate(), 2)
    ].join('-');
}

// Parses ISO date (UTC)
function parseISODate(dateStr) {
    var date = new Date(Date.UTC(+dateStr.substring(0, 4), dateStr.substring(5, 7) - 1, +dateStr.substring(8, 10)));
    if(formatISODate(date) != dateStr) {
        throw new Error("Invalid ISO date: " + dateStr);
    }
    return date;
}

function parseLocalTime(timeStr) {
    if(typeof timeStr === "string" && timeStr.match(/^[0-9]{1,2}:[0-9]{2}$/)) {
        let parts = timeStr.split(":");
        let hours = +parts[0], minutes = +parts[1];
        if(hours >= 0 && hours < 24 && minutes >= 0 && minutes < 60) {
            return { hours, minutes, hhmm: hours * 100 + minutes };
        }
    }
}

function addDays(date, days) {
    var date = new Date();
    date.setDate(date.getDate() + days);
    return date;
}

// Calls a function after a specified number of run loops (default 1)
function defer(fn, runLoops) {
    if(runLoops === undefined) runLoops = 1;

    if(runLoops > 0) {
        setTimeout(function() {
            defer(fn, runLoops - 1);
        });
    } else {
        fn();
    }
}

// Local storage implementation that falls back to cookies (e.g. in private browsing mode on Safari)
var store = {
    get: function(key) {
        var value;

        try {
            value = localStorage[key];
        } catch(e) {
            // do nothing
        }

        if(value === undefined) {
            value = Cookies.get(key);
        }

        return value;
    },
    set: function(key, value) {
        try {
            localStorage[key] = value;
        } catch(e) {
            if(e.code === DOMException.QUOTA_EXCEEDED_ERR && localStorage.length === 0) {
                Cookies.set(key, value);
            } else {
                throw e;
            }
        }
    },
    remove: function(key, value) {
        try {
            localStorage.removeItem(key);
        } catch(e) {
            // do nothing
        }
        Cookies.remove(key);
    }
};

function highlightMatches(text, search, highlightTag) {
    highlightTag = highlightTag || "b";

    var result = document.createElement("div");
    var el;

    var index, matchIndex;

    var lowerText = text.toLowerCase();
    var lowerSearch = search.toLowerCase();

    index = 0;
    while(true) {
        matchIndex = lowerText.indexOf(lowerSearch, index);
        if(matchIndex == -1 || search.length == 0) {
            result.appendChild(document.createTextNode(text.substring(index)));
            break;
        } else {
            result.appendChild(document.createTextNode(text.substring(index, matchIndex)));
            el = document.createElement(highlightTag);
            el.appendChild(document.createTextNode(text.substring(matchIndex, matchIndex + search.length)));
            result.appendChild(el);

            index = matchIndex + search.length;
        }
    }

    return result.innerHTML;
}

// Credit - http://stackoverflow.com/a/14794066/165783
// Checkes if a value is an integer (or string containing integer)
function isInt(value) {
  var x;
  if (isNaN(value)) {
    return false;
  }
  x = parseFloat(value);
  return (x | 0) === x;
}

// Generates a pie-shaped SVG path (e.g. for use as a progress meter)
function piePath(width, radius, fraction) {
    var w = width, r = radius, p = fraction;

    var x = w * (0.5 + r * Math.sin(2 * Math.PI * p));
    var y = w * (0.5 - r * Math.cos(2 * Math.PI * p));
    var x2 = w * (0.5 + r * Math.sin(Math.PI * p));
    var y2 = w * (0.5 - r * Math.cos(Math.PI * p));

    // This returns a rectangle with a pie-shaped hole
    // return _.flatten([
    //      "M", 0, 0,
    //      "L", w, 0,
    //      "L", w, w,
    //      "L", 0, w,
    //      "z",
    //      "M", x, y,
    //      "A", w * r, w * r, 0, 0, 0, x2, y2, // Use two arcs so we can draw a full circle and always use small sweep
    //      "A", w * r, w * r, 0, 0, 0, w / 2, w * (0.5 - r),
    //      (p == 1) ? [] : ["L", w/2, w/2, "z"]
    //  ]).join(" ");

    return [
            "M", x, y,
            "A", w * r, w * r, 0, 0, 0, x2, y2, // Use two arcs so we can draw a full circle and always use small sweep
            "A", w * r, w * r, 0, 0, 0, w / 2, w * (0.5 - r),
            (p == 1) ? [] : ["L", w/2, w/2, "z"]
        ].flat().join(" ");
}

function arrayToMap(arr, keyFn) {
    var map = {};
    arr.forEach(function(x) {
        map[keyFn(x)] = x;
    });
    return map;
}

function downloadString(str, filename, contentType) {
    var a = document.createElement("a");
    a.download = filename;
    a.href = encodeURI("data:" + contentType + ";charset=utf-8," + str);
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
}

// options: accept, multiple
function selectFiles(options) {
    options = options || {};
    return new Promise(function(resolve, reject) {
        var input = document.createElement('input');
        input.setAttribute('style', 'display: none');
        input.setAttribute('type', 'file');
        if(options.accept) {
            input.setAttribute('accept', options.accept);
        }
        if(options.multiple) {
            input.setAttribute('multiple', '');
        }
        document.body.appendChild(input);

        input.addEventListener('change', function() {
            resolve(input.files);
            document.body.removeChild(input);
        });
        input.click();
    });
}

function toggleClass(el, cls, toggle) {
    if(toggle) {
        el.classList.add(cls);
    } else {
        el.classList.remove(cls);
    }
}

function capitalize(str) {
    return str.charAt(0).toUpperCase() + str.substr(1);
}

function makeCache(keyFn, valueFn) {
    var cache = {};
    return function() {
        var key = keyFn.apply(null, arguments);
        if(key in cache) {
            return cache[key];
        } else {
            var value = valueFn.apply(null, arguments);
            cache[key] = value;
            return value;
        }
    };
}

function ancestorSatisfies(element, predicate) {
    while(element) {
        if(predicate(element)) {
            return true;
        }
        element = element.parentElement;
    }
    return false;
}

function monthsShort() {
    return [
        "Jan", "Feb", "Mar", "Apr", "May", "Jun",
        "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
    ]
}

function resolveURL(url) {
    var el = document.createElement("a");
    el.href = url;
    return el.href;
}

function delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
}

function uniq(array) {
    return [...new Set(array)];
}

function isEmptyValue(x) {
    return x === null || x === undefined || (typeof x === "string" && x.trim() == "");
}

function pick(obj, keys) {
    let newObj = {};
    for(let key of keys) {
        newObj[key] = obj[key];
    }
    return newObj;
}

function filterObject(obj, filter) {
    return Object.fromEntries(Object.entries(obj).filter(filter));
}

function sortBy(array, fn) {
    return array.toSorted((a, b) => {
        let aa = fn(a);
        let bb = fn(b);
        return aa > bb ? 1 : aa < bb ? -1 : 0;
    });
}

export default {
    pad: pad,
    assert: assert,
    uuid: uuid,
    parseParams: parseParams,
    hashParams: hashParams,
    queryParams: queryParams,
    encodeParams: encodeParams,
    updateHashParams: updateHashParams,
    escapeHtml: escapeHtml,
    formatDuration: formatDuration,
    formatNumber: formatNumber,
    formatDistance: formatDistance,
    formatQuantity: formatQuantity,
    formatSize: formatSize,
    formatDMS: formatDMS,
    formatDecimalCoordinates: formatDecimalCoordinates,
    formatISODate: formatISODate,
    parseISODate: parseISODate,
    parseLocalTime: parseLocalTime,
    formatDate: formatDate,
    addDays: addDays,
    defer: defer,
    store: store,
    highlightMatches: highlightMatches,
    isInt: isInt,
    piePath: piePath,
    arrayToMap: arrayToMap,
    downloadString: downloadString,
    selectFiles: selectFiles,
    toggleClass: toggleClass,
    capitalize: capitalize,
    makeCache: makeCache,
    ancestorSatisfies: ancestorSatisfies,
    monthsShort: monthsShort,
    resolveURL: resolveURL,
    delay,
    uniq,
    isEmptyValue,
    pick,
    compareNumber,
    compareNumberFn,
    compareStringFn,
    filterObject,
    sortBy
};
